import React, { useContext, useState } from "react";

import {
  Button,
  Container,
  Modal,
  Text,
  Title,
  Stack,
  Group,
  ActionIcon,
  Paper,
  Alert,
  Divider,
  Badge,
  ScrollArea,
  Loader,
  Center,
} from "@mantine/core";
import {
  IconX,
  IconCreditCard,
  IconShield,
  IconCheck,
  IconAlertCircle,
  IconBrandStripe,
} from "@tabler/icons-react";

import { useTranslation } from "react-i18next";

import { usePaystackPayment } from "react-paystack";
import { PaystackProps } from "react-paystack/dist/types";

import {
  InitializePaystackTransactionResponseProps,
  PaymentSuccessResponseProps,
  PlanProps,
  TransactionProps,
  ValidatePaystackTransactionResponseProps,
} from "../../Lib/types";
import "./styles.scss";
import { AppContext } from "../Dashboard";
import { AllFatherContext } from "../../App";
import { PerformRequest } from "../../Lib/usePerformRequest";
import { NonPaginatedResponse } from "../../Lib/responses";
import { Endpoints } from "../../Lib/endpoints";
import { HttpSuccessCodesObj, Colors, PaystackLogo, OperationMode } from "../../Lib/data";
import {
  DefaultErrorNotification,
  DefaultSuccessNotification,
  getCurrencySymbol,
} from "../../Lib/methods";

interface PaystackPaymentModalProps {
  paystack?: InitializePaystackTransactionResponseProps;
  transaction?: TransactionProps;
  opened: boolean;
  onClose: () => void;
  onSuccess: () => void;
  onFailure: () => void;
}

const PaystackPaymentModal: React.FC<PaystackPaymentModalProps> = ({
  paystack,
  transaction,
  opened,
  onClose,
  onSuccess,
  onFailure,
}) => {
  const { t } = useTranslation();

  const context = useContext(AppContext);
  const allFatherContext = useContext(AllFatherContext);

  const paystackConfig: PaystackProps = {
    publicKey:
      OperationMode === "test"
        ? process.env.REACT_APP_PAYSTACK_PUBLIC_TEST ?? ""
        : process.env.REACT_APP_PAYSTACK_PUBLIC_LIVE ?? "",
    email: context?.profile?.email ?? "",
    amount: transaction?.amount ?? 0,
    currency:
      allFatherContext?.locationInformation?.currency ?? localStorage.getItem("currency") ?? "NGN",
    reference: transaction?.reference ?? "",
  };
  const initializePayment = usePaystackPayment(paystackConfig);

  const [isValidating, setValidating] = useState<boolean>(false);
  const ValidatePayment = async () => {
    setValidating(true);

    const r = await PerformRequest<NonPaginatedResponse<ValidatePaystackTransactionResponseProps>>({
      route: Endpoints.GetValidatePaystackPayment(transaction?.reference ?? "").route,
      method: Endpoints.GetValidatePaystackPayment(transaction?.reference ?? "").method,
      callback: () => {
        setValidating(false);
      },
    });
    setValidating(false);
    if (r) {
      if (r.status === HttpSuccessCodesObj.Success) {
        DefaultSuccessNotification("Your payment was succesful!");
        // console.log(r);
        onSuccess();
      } else {
        DefaultErrorNotification("Your payment could not be completed!");
        onFailure();
      }
    } else {
      DefaultErrorNotification("Your payment could not be completed!");
      onFailure();
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      size="lg"
      centered
      withCloseButton={false}
      className="paystack-payment-modal"
      overlayProps={{
        blur: 2,
        opacity: 0.6,
      }}
    >
      {paystackConfig && paystackConfig.reference ? (
        <Container size="md" py="xl">
          <ScrollArea h={500}>
            {/* Header */}
            <Group justify="space-between" align="center" mb="xl">
              <div>
                <img src={PaystackLogo} className="paystack-logo" />
                <Text c="dimmed" size="sm" className="modal-subtitle">
                  Secure payment powered by Paystack
                </Text>
              </div>
              <ActionIcon variant="subtle" size="lg" onClick={onClose} className="close-button">
                <IconX size={20} />
              </ActionIcon>
            </Group>

            {/* Payment Details */}
            {transaction && (
              <Paper shadow="sm" p="lg" radius="md" withBorder mb="xl" className="payment-details">
                <Stack gap="md">
                  <Group justify="space-between" align="center">
                    <div>
                      <Text size="sm" c="dimmed" mb={4}>
                        Payment Reference
                      </Text>
                      <Text fw={600} size="sm" className="reference-text">
                        {transaction.reference}
                      </Text>
                    </div>
                  </Group>

                  <Divider />

                  <Group justify="space-between" align="center">
                    <div>
                      <Text size="sm" c="dimmed" mb={4}>
                        Amount
                      </Text>
                      <Text fw={700} size="xl" c={Colors.GreenPrimary} className="amount-text">
                        {getCurrencySymbol(transaction.currency)}
                        {(transaction.amount / 100).toLocaleString()}
                      </Text>
                    </div>
                  </Group>
                </Stack>
              </Paper>
            )}

            {/* Customer Information */}
            {context?.profile && (
              <Paper shadow="sm" p="lg" radius="md" withBorder mb="xl" className="customer-info">
                <Stack gap="md">
                  <div className="flex-col">
                    <div>
                      <Text size="sm" c="dimmed" mb={4}>
                        Name
                      </Text>
                      <Text fw={600}>{context.profile.name || "Student"}</Text>
                    </div>
                    <Divider my={5} color={Colors.GrayTertiary} />
                    <div>
                      <Text size="sm" c="dimmed" mb={4}>
                        Email
                      </Text>
                      <Text fw={600}>{context.profile.email}</Text>
                    </div>
                  </div>
                </Stack>
              </Paper>
            )}

            {/* Security Notice */}
            <Alert
              icon={<IconShield size={16} />}
              color="blue"
              variant="light"
              title="Secure Payment"
              mb="xl"
              className="security-alert"
            >
              <Text size="sm">
                Your payment is processed securely by Paystack. We never store your payment
                information. All transactions are encrypted and protected.
              </Text>
            </Alert>
          </ScrollArea>

          {/* Payment Button */}
          <Stack gap="md" className="payment-section">
            <Button
              fullWidth
              size="md"
              onClick={() => {
                console.log("Paystack config", paystackConfig);
                initializePayment({
                  onSuccess: (e: PaymentSuccessResponseProps) => {
                    if (e && e.status === "success") {
                      ValidatePayment();
                    }
                  },
                  onClose: (e) => {
                    console.log("Payment closed", e);
                  },
                });
              }}
              leftSection={<IconCreditCard size={18} />}
              loading={isValidating}
            >
              {isValidating ? "Processing Payment..." : "Pay with Paystack"}
            </Button>

            <Text size="xs" c="dimmed" ta="center" className="terms-text">
              By clicking "Pay with Paystack", you agree to our terms of service and privacy policy
            </Text>
          </Stack>
        </Container>
      ) : (
        <Center>
          <Loader />
        </Center>
      )}
    </Modal>
  );
};

export default PaystackPaymentModal;
